import { useEffect, useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Send } from 'lucide-react';
import useApi from '../../api';
import MentionEditor from '../builder/MentionEditor';
import { buildMentionables, MentionableUser } from '../builder/promptMentions';
import useObjectiveMentions from '../builder/useObjectiveMentions';
import AgentTabHeader from './AgentTabHeader';
import { ProactiveTabProps } from './types';

// Proactive engagement is versioned agent config (unlike the Email tab):
// changes ride the normal draft/publish flow and take effect on publish.

export default function ProactiveTab({
    agId,
    setNeedsUpdate,
    proactiveEnabled,
    setProactiveEnabled,
    proactiveMinInactivityHours,
    setProactiveMinInactivityHours,
    proactiveMaxUnanswered,
    setProactiveMaxUnanswered,
    proactiveEmailFallback,
    setProactiveEmailFallback,
    proactiveInstructions,
    setProactiveInstructions,
    proactiveVisitorsEnabled,
    setProactiveVisitorsEnabled,
    proactiveVisitorsMinInactivityHours,
    setProactiveVisitorsMinInactivityHours,
    proactiveVisitorsMaxUnanswered,
    setProactiveVisitorsMaxUnanswered,
    proactiveVisitorsDailyCap,
    setProactiveVisitorsDailyCap,
    proactiveVisitorsInstructions,
    setProactiveVisitorsInstructions,
    form,
    skills,
}: ProactiveTabProps) {
    const { get } = useApi();
    // Org users for Team @mentions (InstructionsTab idiom: builder tabs are
    // self-sufficient about their mention data sources).
    const [organizationUsers, setOrganizationUsers] = useState<MentionableUser[]>([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const usersResponse = await get('/users/');
                if (!cancelled) setOrganizationUsers(usersResponse.data || []);
            } catch (error) {
                console.error('Error fetching organization users:', error);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const objectives = useObjectiveMentions(agId);
    const mentionables = useMemo(
        () => buildMentionables(form || [], skills || [], { users: organizationUsers, objectives }),
        [form, skills, organizationUsers, objectives],
    );

    return (
        <div className="min-w-0 max-w-full">
            <AgentTabHeader
                icon={Send}
                title="Proactive"
                description="Let the agent reach out on its own: to signed-in users it knows, and to anonymous website visitors whose browsing shows intent."
            />

            <div className="mx-auto min-w-0 w-full max-w-4xl space-y-10 py-6">
                {/* ---------------------------------------------------------------- */}
                {/* Signed-in users (app surface / identified contacts)               */}
                {/* ---------------------------------------------------------------- */}
                <section className="space-y-6">
                    <div className="flex items-start justify-between gap-4 border-b pb-3">
                        <div>
                            <h2 className="text-sm font-semibold">Signed-in users</h2>
                            <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                Identified contacts: a first-session welcome, or a nudge when they come
                                back after being away. The agent can use their account, objectives,
                                CRM and product data.
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <Label htmlFor="proactive-toggle" className="cursor-pointer text-xs text-muted-foreground">
                                {proactiveEnabled ? 'Enabled' : 'Disabled'}
                            </Label>
                            <Switch
                                id="proactive-toggle"
                                checked={proactiveEnabled}
                                onCheckedChange={(value) => {
                                    setProactiveEnabled(value);
                                    setNeedsUpdate(true);
                                }}
                            />
                        </div>
                    </div>

                {!proactiveEnabled && (
                    <div className="rounded-lg border bg-card p-6 text-center">
                        <p className="text-sm font-medium">Proactive engagement is off</p>
                        <p className="mx-auto mt-1 max-w-lg text-xs leading-5 text-muted-foreground">
                            Turn it on and the agent will review each identified contact when they
                            start their first session or return after being away — and decide
                            whether a short, specific message would help them move forward. It
                            stays quiet when it has nothing useful to say, and every decision
                            (sent or skipped, with the reason) is recorded.
                        </p>
                    </div>
                )}

                <div className="space-y-3">
                    <div>
                        <h3 className="text-sm font-semibold">When to consider reaching out</h3>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            The agent only ever considers identified contacts, at most once per
                            day each. It always reviews a contact&apos;s first identified session.
                        </p>
                    </div>
                    <div className="rounded-lg border bg-card p-4 space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="proactive-gap" className="text-xs">
                                Minimum time away before a return counts (hours)
                            </Label>
                            <Input
                                id="proactive-gap"
                                type="number"
                                min={1}
                                max={720}
                                value={proactiveMinInactivityHours}
                                onChange={(e) => setProactiveMinInactivityHours(e.target.value)}
                                className="w-32"
                            />
                            <p className="text-[11px] text-muted-foreground">
                                A contact coming back after at least this long triggers a review.
                                Default 12 hours — roughly &quot;a new day, new session&quot;.
                            </p>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="proactive-max-unanswered" className="text-xs">
                                Stop after this many unanswered messages
                            </Label>
                            <Input
                                id="proactive-max-unanswered"
                                type="number"
                                min={1}
                                max={20}
                                value={proactiveMaxUnanswered}
                                onChange={(e) => setProactiveMaxUnanswered(e.target.value)}
                                className="w-32"
                            />
                            <p className="text-[11px] text-muted-foreground">
                                Once this many proactive messages in a row get no reply, the agent
                                stays quiet until the contact says something. Default 3.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    <div>
                        <h3 className="text-sm font-semibold">Guidance</h3>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            Tell the agent who to reach out to, when to stay quiet, and what a
                            good message looks like. Type @ to reference fields, objectives,
                            lifecycle stage, skills, tools, or teammates — references resolve
                            to live values when the agent decides.
                        </p>
                    </div>
                    <div className="rounded-lg border bg-card p-4">
                        {/* Typed inputs deliberately do not set needsUpdate — that
                            effect fires a save per change, which per-keystroke
                            editors would spam. They save on the explicit Save
                            action, like the Instructions prompts. */}
                        <MentionEditor
                            value={proactiveInstructions}
                            onChange={setProactiveInstructions}
                            mentionables={mentionables}
                            placeholder={
                                'Example: Only send proactive messages to contacts whose ' +
                                '@ lifecycle stage is Trial. Focus every nudge on the next ' +
                                'incomplete onboarding objective. Never message admins about billing.'
                            }
                            className="min-w-0 font-mono [&_.ProseMirror]:min-h-[160px]"
                        />
                        <p className="mt-2 text-[11px] text-muted-foreground">
                            A contact outside this guidance is always skipped, and the skip
                            reason is recorded on the run. Leave empty to use the default
                            behavior: nudge only when there&apos;s a specific, useful next step.
                        </p>
                    </div>
                </div>

                <div className="space-y-3">
                    <div>
                        <h3 className="text-sm font-semibold">Delivery</h3>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            Messages are sent as a normal conversation from this agent. If the
                            contact is online they see it immediately; otherwise it opens on
                            their next visit.
                        </p>
                    </div>
                    <div className="rounded-lg border bg-card p-4">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-sm font-medium">Email when offline</p>
                                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                    If the contact is offline when a message is ready, send it by
                                    email instead (requires this agent&apos;s Email channel to be
                                    enabled). Replies come back to the agent as a conversation.
                                </p>
                            </div>
                            <Switch
                                checked={proactiveEmailFallback}
                                onCheckedChange={(value) => {
                                    setProactiveEmailFallback(value);
                                    setNeedsUpdate(true);
                                }}
                            />
                        </div>
                    </div>
                </div>
                </section>

                {/* ---------------------------------------------------------------- */}
                {/* Website visitors (anonymous, warm-visitor trigger)                 */}
                {/* ---------------------------------------------------------------- */}
                <section className="space-y-6">
                    <div className="flex items-start justify-between gap-4 border-b pb-3">
                        <div>
                            <h2 className="text-sm font-semibold">Website visitors</h2>
                            <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                Anonymous visitors whose browsing shows buying intent: a pricing, demo or
                                contact page, product-page depth, a return visit or a buying call to
                                action. The agent only ever sees what they did on the site, never who
                                they are, even when the company has been identified.
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <Label htmlFor="proactive-visitors-toggle" className="cursor-pointer text-xs text-muted-foreground">
                                {proactiveVisitorsEnabled ? 'Enabled' : 'Disabled'}
                            </Label>
                            <Switch
                                id="proactive-visitors-toggle"
                                checked={proactiveVisitorsEnabled}
                                onCheckedChange={(value) => {
                                    setProactiveVisitorsEnabled(value);
                                    setNeedsUpdate(true);
                                }}
                            />
                        </div>
                    </div>

                    {!proactiveVisitorsEnabled && (
                        <div className="rounded-lg border bg-card p-6 text-center">
                            <p className="text-sm font-medium">Website visitor engagement is off</p>
                            <p className="mx-auto mt-1 max-w-lg text-xs leading-5 text-muted-foreground">
                                Turn it on and the agent will review each visitor the moment their
                                browsing turns warm, and again when a warm visitor comes back after
                                being away. It writes about what they looked at, never who they are.
                                If they have already left, the message opens on their next visit.
                            </p>
                        </div>
                    )}

                    <div className="space-y-3">
                        <div>
                            <h3 className="text-sm font-semibold">When to consider reaching out</h3>
                            <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                At most once per visitor per day. Known contacts are handled by the
                                signed-in rules above. When account research is on and the visitor&apos;s
                                company was already identified and scored below your fit threshold, the
                                agent stays quiet.
                            </p>
                        </div>
                        <div className="rounded-lg border bg-card p-4 space-y-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="proactive-visitors-gap" className="text-xs">
                                    Minimum time away before a return counts (hours)
                                </Label>
                                <Input
                                    id="proactive-visitors-gap"
                                    type="number"
                                    min={1}
                                    max={720}
                                    value={proactiveVisitorsMinInactivityHours}
                                    onChange={(e) => setProactiveVisitorsMinInactivityHours(e.target.value)}
                                    className="w-32"
                                />
                                <p className="text-[11px] text-muted-foreground">
                                    A warm visitor coming back after at least this long, and viewing a
                                    second page, triggers another review. Default 12 hours.
                                </p>
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="proactive-visitors-max-unanswered" className="text-xs">
                                    Stop after this many unanswered messages
                                </Label>
                                <Input
                                    id="proactive-visitors-max-unanswered"
                                    type="number"
                                    min={1}
                                    max={20}
                                    value={proactiveVisitorsMaxUnanswered}
                                    onChange={(e) => setProactiveVisitorsMaxUnanswered(e.target.value)}
                                    className="w-32"
                                />
                                <p className="text-[11px] text-muted-foreground">
                                    Once this many messages in a row get no reply, the agent stays quiet
                                    until the visitor says something. Default 2.
                                </p>
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="proactive-visitors-daily-cap" className="text-xs">
                                    Daily limit (reviews per day for this agent)
                                </Label>
                                <Input
                                    id="proactive-visitors-daily-cap"
                                    type="number"
                                    min={1}
                                    max={1000}
                                    value={proactiveVisitorsDailyCap}
                                    onChange={(e) => setProactiveVisitorsDailyCap(e.target.value)}
                                    className="w-32"
                                />
                                <p className="text-[11px] text-muted-foreground">
                                    Each review is a metered AI run. This caps how many warm visitors
                                    the agent considers per day, so a busy site can&apos;t spend credits
                                    unattended. Default 50.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <h3 className="text-sm font-semibold">Guidance</h3>
                            <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                Tell the agent when to reach out to a visitor, when to stay quiet, and
                                what a good opener looks like. Identity references don&apos;t apply here:
                                the agent has nothing but browsing behavior to go on.
                            </p>
                        </div>
                        <div className="rounded-lg border bg-card p-4">
                            <Textarea
                                value={proactiveVisitorsInstructions}
                                onChange={(e) => setProactiveVisitorsInstructions(e.target.value)}
                                placeholder={
                                    'Example: Only reach out to visitors who viewed pricing or a ' +
                                    'comparison page. Offer to answer the question that page raises ' +
                                    'and point them at the right plan. Never mention that we can see ' +
                                    'their browsing history.'
                                }
                                className="min-h-[140px] text-sm"
                            />
                            <p className="mt-2 text-[11px] text-muted-foreground">
                                A visitor outside this guidance is always skipped, and the skip reason
                                is recorded on the run. Leave empty to use the default behavior: reach
                                out only when the pages they viewed give the agent something specific
                                to offer.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <h3 className="text-sm font-semibold">Delivery</h3>
                            <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                Messages are sent as a normal conversation from this agent. If the
                                visitor is still on the site they see it immediately; otherwise it opens
                                on their next visit within a week. There is no email fallback: anonymous
                                visitors have no email.
                            </p>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}
